/**
 * Thin client for the `/api/fan/*` + `/api/characters/*` endpoints the
 * fan platform calls. All requests carry the Fan Cognito id-token via
 * Authorization: Bearer when present; anonymous calls are allowed for
 * access-check + public character info.
 *
 * Env (Vite-baked at build time):
 *   VITE_API_BASE_URL   absolute base e.g. https://<id>.execute-api.<region>.amazonaws.com
 */
import { getStoredFanIdToken, refreshFanTokenIfNeeded, fanCognitoConfigured } from '../auth/fan-auth';

/**
 * Hard-navigate the browser to the in-app /login page. Used by the
 * 401 handler so the fan lands on the custom login form (not the
 * Cognito Hosted UI). Preserves the current path as `?from=` so the
 * Login page can bounce the user back after sign-in.
 */
function bounceToLogin(): void {
  if (typeof window === 'undefined') return;
  if (window.location.pathname === '/login') return;
  const from = window.location.pathname + window.location.search;
  const target = from === '/' ? '/login' : `/login?from=${encodeURIComponent(from)}`;
  window.location.assign(target);
}

function apiBase(): string {
  const base = (import.meta as unknown as { env?: Record<string, string | undefined> }).env?.VITE_API_BASE_URL;
  if (!base) throw new Error('VITE_API_BASE_URL not set');
  return base.replace(/\/+$/, '');
}

async function authHeaders(): Promise<Record<string, string>> {
  await refreshFanTokenIfNeeded();
  const tok = getStoredFanIdToken();
  return tok ? { Authorization: `Bearer ${tok}` } : {};
}

/**
 * Routes 401 responses to the Cognito sign-in flow when the call was
 * supposed to be auth'd. Callers that pass `allow401: true` (e.g. the
 * anonymous-OK browse APIs) get the regular thrown error so they can
 * fall back to anonymous behaviour.
 */
async function request<T>(path: string, init?: RequestInit & { allow401?: boolean }): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(await authHeaders()),
    ...((init?.headers as Record<string, string>) ?? {}),
  };
  const res = await fetch(`${apiBase()}${path}`, { ...init, headers });
  // 401 = missing/invalid auth header. 403 = APIGW CUSTOM authorizer
  // rejected (token signature failed, expired, wrong pool, etc).
  // Both mean "fan needs to sign in again" from the SPA's POV — the
  // backend already rejected, no point re-asking. Without the 403
  // branch, expired-token sessions show `{"message":"Forbidden"}` in
  // an error banner instead of bouncing the user to the login form
  // (reported by user 2026-05-27).
  if ((res.status === 401 || res.status === 403) && !init?.allow401) {
    if (fanCognitoConfigured()) {
      bounceToLogin();
      return new Promise<T>(() => { /* deliberately never resolves */ });
    }
  }
  if (!res.ok) {
    let detail = '';
    try { detail = await res.text(); } catch { /* ignore */ }
    // Extract the API error `code` (e.g. CHARACTER_DELISTED) so callers
    // can branch on a stable identifier rather than parsing the message.
    let code: string | undefined;
    try {
      const parsed = JSON.parse(detail) as { code?: string; error?: { code?: string } };
      code = parsed.code ?? parsed.error?.code;
    } catch { /* non-JSON body — leave code undefined */ }
    const err = new Error(`${res.status} ${res.statusText} ${detail.slice(0, 400)}`);
    (err as Error & { status?: number; code?: string }).status = res.status;
    (err as Error & { status?: number; code?: string }).code = code;
    throw err;
  }
  if (res.status === 204) return undefined as unknown as T;
  return (await res.json()) as T;
}

// ── Character discovery ─────────────────────────────────────────
export interface FanCharacterCard {
  id: string;
  name: string;
  /** Operator-authored character introduction from the admin backend. */
  description?: string;
  greetingText?: string;
  tags?: string[];
  thumbnailUrl?: string;
  type?: 'pngtuber' | 'model3d' | 'live2d';
  modelPath?: string;
  viewportRotationY?: number;
  clipLabels?: Record<string, string>;
  defaultClipLabels?: string[];
  accessReason?: 'owned' | 'my-group' | 'my-individual' | 'public';
  pricing?: { text?: number; voice?: number };
  /** Legacy single wallet-group field. New code should read
   *  walletGroupIds; this is kept for back-compat with older API
   *  builds that don't surface the array yet. */
  walletGroupId?: string;
  /** All wallet groups the fan can self-enroll into for this
   *  character. When the array has >1 entry, the enroll call should
   *  pass a chosen groupId in the body. */
  walletGroupIds?: string[];
  modelDistribution?: {
    enabled: boolean;
    mode: 'free' | 'paid' | 'coming-soon';
    pricePoints?: number;
  };
  /** Operator-published banner / promo photos. Backend filters out
   *  hidden + card-only rows; the SPA can blindly render whatever
   *  arrives here. Empty/undefined → fall back to thumbnailUrl. */
  gallery?: Array<{
    imageId: string;
    url: string;
    caption?: string;
    sortOrder?: number;
  }>;
  /** URL of the operator's maintenance-status image. Used when an
   *  open incident is detected on this character. */
  maintenanceImageUrl?: string;
  /** Operator (公司/品牌) display name — shown as small text next to
   *  the character name on the CharacterMarket page. Resolved server-
   *  side from operators table (profile.companyName → email prefix). */
  operatorDisplayName?: string;
}

/** Per `character-incident-monitor` — fan-side check that swaps the
 *  banner / blocks chat with a maintenance overlay when this
 *  character has an open upstream-failure incident. Anonymous-OK. */
export interface FanCharacterIncident {
  characterId: string;
  kind: 'tts-upstream' | 'llm-upstream' | 'asr-upstream' | 'voice-upstream';
  firstSeenAt: string;
}

// ── nfc-point-cards ──────────────────────────────────────────────
export interface PointCardPeek {
  status: 'active' | 'redeemed' | 'void';
  faceValue: number;
  currency: 'operator' | 'platform';
  characterId: string;
  characterName: string;
  /** 送角色卡 — redeeming also grants ownership of characterId. */
  grantCharacter?: boolean;
  /** Only meaningful when grantCharacter — fan already owns it (points only). */
  alreadyOwned?: boolean;
  redeemedByMe: boolean;
}
export async function peekPointCard(code: string): Promise<PointCardPeek> {
  return request<PointCardPeek>(`/api/fan/point-cards/${encodeURIComponent(code)}`, { allow401: true });
}
export async function redeemPointCard(code: string): Promise<{
  credited?: number; currency?: string; characterId?: string; newBalance?: number; alreadyRedeemed?: boolean;
  characterGranted?: boolean; characterAlreadyOwned?: boolean;
}> {
  return request(`/api/fan/point-cards/redeem`, { method: 'POST', body: JSON.stringify({ code }) });
}

// ── 儲值紀錄(topup order history)────────────────────────────────
export interface MyTopupOrder {
  tradeNo: string;
  amountTwd: number;
  pointsCredited: number;
  status: 'pending' | 'success' | 'failed' | string;
  paymentMethod: string;
  createdAt: string;
  updatedAt: string;
}
/** 自己的儲值訂單(最新在前,最多 50 筆)。 */
export async function listMyTopupOrders(): Promise<MyTopupOrder[]> {
  const out = await request<{ items: MyTopupOrder[] }>('/api/fan/wallet/topup/orders');
  return out.items ?? [];
}

// ── fan-game-bridge — 角色頁「功能」遊戲入口 ──────────────────────
export interface CharacterGameTile {
  gameId: string;
  name: string;
  iconUrl: string | null;
  description: string | null;
}
/** 該角色 scope 命中且啟用中的遊戲(後台遊戲註冊表自動同步)。 */
export async function listCharacterGames(characterId: string): Promise<CharacterGameTile[]> {
  const out = await request<{ items: CharacterGameTile[] }>(
    `/api/fan/games/character/${encodeURIComponent(characterId)}`,
    { allow401: true },
  );
  return out.items ?? [];
}
/** 簽發單次 launch token 並取得啟動網址(5 分鐘內有效)。 */
export async function launchGame(gameId: string, characterId: string): Promise<{ launchUrl: string }> {
  return request(`/api/fan/games/${encodeURIComponent(gameId)}/launch`, {
    method: 'POST',
    body: JSON.stringify({ characterId }),
  });
}

export async function getCharacterIncident(characterId: string): Promise<FanCharacterIncident | null> {
  const out = await request<{ incident: FanCharacterIncident | null }>(
    `/api/fan/characters/${encodeURIComponent(characterId)}/incident`,
    { allow401: true },
  );
  return out.incident;
}

/** Per `voice-experiment-framework` (voice-loop iter 1) — fan SPA
 *  reads the operator's kill-switch list. Default-on for any missing
 *  key (callers should pass `defaultValue=true` to the helper). */
export interface PublicVoiceFlag { flagKey: string; enabled: boolean }

export async function listVoiceFlags(): Promise<PublicVoiceFlag[]> {
  const out = await request<{ flags: PublicVoiceFlag[] }>(
    '/api/voice-flags',
    { allow401: true },
  );
  return out.flags;
}

/** Owned-skin row from GET /api/fan/characters/mine. */
export interface OwnedSkin {
  characterId: string;
  acquiredAt: string;
  mode: 'free' | 'paid';
}

export async function listMyOwnedCharacters(): Promise<{ items: OwnedSkin[] }> {
  return request<{ items: OwnedSkin[] }>('/api/fan/my-characters');
}

/** Idempotent claw-back of a chat-spend. Called when a paid-for
 *  post-step (voice-mode TTS in particular) fails on the client side
 *  after the chat handler already debited the wallet. Server enforces
 *  one-refund-per-txnKey via ConditionExpression so retries are safe.
 *  Returns true on success OR 409 (already refunded), false on any
 *  other failure — caller should not block UX on the result. */
export async function refundChatSpend(opts: {
  txnKey: string;
  reason: string;
}): Promise<boolean> {
  try {
    await request<{ refunded: boolean }>('/api/fan/wallet/chat-refund', {
      method: 'POST',
      body: JSON.stringify(opts),
      headers: { 'content-type': 'application/json' },
    });
    return true;
  } catch (e) {
    const msg = (e as Error).message ?? '';
    // 409 = already refunded — idempotent success.
    if (msg.includes('ALREADY_REFUNDED')) return true;
    console.warn('[fan-api] refundChatSpend failed', e);
    return false;
  }
}

export async function acquireCharacter(characterId: string): Promise<{
  acquired: boolean;
  alreadyOwned?: boolean;
  mode?: 'free' | 'paid';
  pricePoints?: number;
}> {
  return request(
    `/api/fan/characters/${encodeURIComponent(characterId)}/acquire`,
    { method: 'POST', body: '{}' },
  );
}
export async function listFanCharacters(): Promise<{ items: FanCharacterCard[] }> {
  return request<{ items: FanCharacterCard[] }>('/api/fan/characters');
}
export async function getFanCharacter(id: string): Promise<FanCharacterCard> {
  return request<FanCharacterCard>(`/api/fan/characters/${encodeURIComponent(id)}`);
}

// ── Access check ───────────────────────────────────────────────
export interface AccessCheckResult {
  allowed: boolean;
  reason: string;
  fanMode?: string;
  policyMode?: string;
}
export async function checkAccess(characterId: string): Promise<AccessCheckResult> {
  return request<AccessCheckResult>(`/api/characters/${encodeURIComponent(characterId)}/access-check`);
}

// ── Profile ────────────────────────────────────────────────────
export type FanGender = 'male' | 'female' | 'other' | 'prefer-not-to-say';
export interface FanLinkedYouTube {
  channelId: string;
  channelTitle: string;
  thumbnailUrl: string;
  linkedAt: string;
}
export interface FanLinkedAuthProvider {
  provider: 'password' | 'google';
  linkedAt: string;
}
export interface FanProfile {
  fanId: string;
  email: string;
  displayName: string;
  locale: string;
  birthday?: string;
  gender?: FanGender;
  /** Absent on legacy rows + brand-new self-healed rows. SPA shows the
   *  post-signup setup modal whenever this field is missing. */
  profileCompletedAt?: string;
  /** Per `fan-youtube-integration` — populated by the YT link flow. */
  linkedYouTube?: FanLinkedYouTube;
  /** Cognito-identity summary derived server-side. */
  linkedAuthProviders?: FanLinkedAuthProvider[];
  createdAt: string;
  updatedAt: string;
}
export async function getMyProfile(): Promise<FanProfile> {
  return request<FanProfile>('/api/fan/me');
}

/** Per `fan-youtube-integration` — initiate the YT link OAuth round-trip. */
export async function startYoutubeLink(): Promise<{ authUrl: string }> {
  return request<{ authUrl: string }>('/api/fan/profile/link-youtube', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: '{}',
  });
}

export async function unlinkYoutube(): Promise<void> {
  await request<void>('/api/fan/profile/link-youtube', { method: 'DELETE' });
}

export type FanCharacterMembership =
  | { isMember: true; level: string; levelDisplayName: string; since: string; source: 'cache'; cacheAgeSec: number }
  | { isMember: false; reason: 'no-linked-youtube' | 'character-has-no-channel' | 'feature-disabled' | 'cache-pending' | 'not-a-member'; source: 'cache'; cacheAgeSec: number | null };

export async function getCharacterMembership(characterId: string): Promise<FanCharacterMembership> {
  return request<FanCharacterMembership>(`/api/fan/characters/${encodeURIComponent(characterId)}/youtube-membership`);
}

/**
 * Fire-and-forget login-time refresh hook. Async-invokes the YT
 * members poller on the backend, filtered to channels this fan has
 * character-access to. The poller updates the cache; the fan's
 * subsequent reads via `getCharacterMembership` will see fresher data.
 *
 * Silently swallows errors — refresh failures shouldn't block the
 * SPA's render after a successful login.
 */
export async function refreshMyYoutubeMemberships(): Promise<void> {
  try {
    await request<{ refreshed: number }>('/api/fan/yt/refresh-mine', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: '{}',
    });
  } catch {
    /* fire-and-forget */
  }
}
export interface FanProfileUpdate {
  displayName: string;
  birthday?: string;
  gender?: FanGender;
}
export async function updateMyProfile(body: FanProfileUpdate): Promise<FanProfile> {
  return request<FanProfile>('/api/fan/me', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}
export async function deleteMyAccount(): Promise<void> {
  return request<void>('/api/fan/me', { method: 'DELETE' });
}

// ── Affinity / Relationships (fan-affinity-system) ─────────────
export type AffinityLevelName = '陌生' | '點頭之交' | '朋友' | '親近' | '摯友' | '靈魂相依';
export interface AffinityMilestone {
  level: number;
  levelName: AffinityLevelName;
  crossedAt: string;
  context?: string;
}
export interface RelationshipListItem {
  characterId: string;
  characterName: string;
  value: number;
  level: number;
  levelName: AffinityLevelName;
  lastInteractionAt: string;
  streakDays: number;
  memoryCardPreview: string | null;
  milestoneCount: number;
}
export interface LevelUnlock {
  level: number;
  levelName: AffinityLevelName;
  conversationStarter: string;
  characterQuote: string;
  stickerEmoji: string;
  stickerUrl: string | null;
  voiceUrl: string | null;
}
export interface RelationshipDetail {
  characterId: string;
  characterName: string;
  value: number;
  level: number;
  levelName: AffinityLevelName;
  valueToNextLevel: number | null;
  valueRangeOfCurrentLevel: [number, number];
  streakDays: number;
  lastInteractionAt: string;
  memoryCard: string | null;
  memoryCardUpdatedAt: string | null;
  milestones: AffinityMilestone[];
  dailyGreetAvailable: boolean;
  dailyGreetMessage: string | null;
  dailyGreetReward: number;
  dailyGreetHolidayId: string | null;
  dailyGreetHolidayName: string | null;
  dailyGreetHolidayEmoji: string | null;
  moodCheckinAvailable: boolean;
  todayMoodEmoji: string | null;
  todayMoodNote: string | null;
  personalizedGreetingBucket: string | null;
  personalizedGreetingHint: string | null;
  /** v2 tier (1-10), resolved with operator's per-character override. */
  tierV2?: number;
  tierV2Name?: string;
  tierV2Description?: string;
  unlocks: LevelUnlock[];
}
export interface MoodCheckinResponse {
  recorded: boolean;
  creditedDelta: number;
  newValue: number;
  newLevel: number;
  newLevelName: AffinityLevelName;
  leveledUp: boolean;
  streakDays: number;
  emoji: string;
  note: string | null;
}
export async function postMoodCheckin(
  characterId: string,
  body: { emoji: string; note?: string },
): Promise<MoodCheckinResponse> {
  return request<MoodCheckinResponse>(
    `/api/fan/relationships/${encodeURIComponent(characterId)}/mood-checkin`,
    {
      method: 'POST',
      body: JSON.stringify(body),
    },
  );
}
export interface DailyGreetRespondResponse {
  responded: boolean;
  creditedDelta: number;
  newValue: number;
  newLevel: number;
  newLevelName: AffinityLevelName;
  leveledUp: boolean;
  streakDays: number;
  holidayId?: string | null;
  holidayName?: string | null;
}
export async function respondToDailyGreet(characterId: string): Promise<DailyGreetRespondResponse> {
  return request<DailyGreetRespondResponse>(
    `/api/fan/relationships/${encodeURIComponent(characterId)}/daily-greet/respond`,
    { method: 'POST' },
  );
}
export interface WeeklyDashboard {
  week: Array<{ date: string; active: boolean }>;
  longestStreak: number;
  currentMultiplier: number;
}
export async function listMyRelationships(): Promise<{ items: RelationshipListItem[]; dashboard: WeeklyDashboard }> {
  return request<{ items: RelationshipListItem[]; dashboard: WeeklyDashboard }>('/api/fan/relationships');
}
export async function getMyRelationship(characterId: string): Promise<RelationshipDetail> {
  return request<RelationshipDetail>(`/api/fan/relationships/${encodeURIComponent(characterId)}`);
}

// ── Pomodoro mini-app ─────────────────────────────────────────
export interface PomodoroStartResponse {
  sessionId: string;
  startedAt: string;
  durationSec: number;
  expiresAtSec: number;
}
export interface PomodoroCompleteResponse {
  completed: boolean;
  creditedDelta: number;
  newValue: number | null;
  newLevel?: number;
  newLevelName?: AffinityLevelName;
  leveledUp: boolean;
  streakDays?: number;
  warning?: string;
}
export async function startPomodoro(characterId: string): Promise<PomodoroStartResponse> {
  return request<PomodoroStartResponse>('/api/fan/pomodoro/start', {
    method: 'POST',
    body: JSON.stringify({ characterId }),
  });
}
export async function completePomodoro(sessionId: string): Promise<PomodoroCompleteResponse> {
  return request<PomodoroCompleteResponse>(`/api/fan/pomodoro/${encodeURIComponent(sessionId)}/complete`, {
    method: 'POST',
  });
}

// ── Groups (fan-side) ──────────────────────────────────────────
export interface FanGroupRow {
  groupId: string;
  name: string;
  description?: string;
  customerId: string;
  joinedAt: string;
}
export async function listMyFanGroups(): Promise<{ items: FanGroupRow[] }> {
  return request<{ items: FanGroupRow[] }>('/api/fan/groups');
}

// ── Character energy (battery) — `character-energy-system` ─────────
export interface EnergyView {
  energy: number; capacity: number; pct: number; rawPct: number;
  regenPerHour: number; full: boolean; passActive: boolean;
  idleImageUrl?: string; // operator-uploaded idle screen (on GET /energy)
}
export interface EnergyShopItem {
  id: string; type: string; name: string; description?: string;
  pricePoints: number; amount?: number; bonus?: number; capacityDelta?: number;
  boostRegenPct?: number; durationDays?: number;
}
const ENERGY = (id: string) => `/api/fan/characters/${encodeURIComponent(id)}/energy`;
export async function getEnergy(characterId: string): Promise<EnergyView> {
  return request<EnergyView>(ENERGY(characterId));
}
export async function tickEnergy(characterId: string): Promise<EnergyView & { idle: boolean }> {
  return request<EnergyView & { idle: boolean }>(`${ENERGY(characterId)}/tick`, { method: 'POST' });
}
export async function resumeEnergy(characterId: string): Promise<EnergyView> {
  return request<EnergyView>(`${ENERGY(characterId)}/resume`, { method: 'POST' });
}
export async function rechargeEnergy(characterId: string, points: number): Promise<EnergyView> {
  return request<EnergyView>(`${ENERGY(characterId)}/recharge`, { method: 'POST', body: JSON.stringify({ points }) });
}
export async function getEnergyShop(characterId: string): Promise<{ items: EnergyShopItem[]; energy: EnergyView }> {
  return request<{ items: EnergyShopItem[]; energy: EnergyView }>(`${ENERGY(characterId)}/shop`);
}
export async function purchaseEnergyShop(characterId: string, itemId: string): Promise<EnergyView> {
  return request<EnergyView>(`${ENERGY(characterId)}/shop/purchase`, { method: 'POST', body: JSON.stringify({ itemId }) });
}

// ── Wallet ─────────────────────────────────────────────────────
export interface PlatformBalance { balance: number; updatedAt: string }
export interface GroupBalance { groupId: string; groupName: string; balance: number; updatedAt: string }
/** Pre-v3 operator wallet. Still surfaced read-only while pre-
 *  migration balances exist; will be empty for everyone post-
 *  migration. */
export interface OperatorBalance {
  operatorId: string;
  balance: number;
  updatedAt: string;
}
/** v3 character wallet — balance follows the IP; the current
 *  operator + currency name are just display metadata. */
export interface CharacterBalance {
  characterId: string;
  characterName: string;
  operatorId?: string;
  operatorDisplayName?: string;
  currencyName: string;
  currencyIconUrl?: string;
  balance: number;
  updatedAt: string;
}
/** v4 operator-scope wallet card. Per-operator balance with the
 *  operator's chosen currency name + icon; characters listed as
 *  small badge row (info only — chat-spend debits this single
 *  balance for any character under this operator). */
export interface OperatorCard {
  operatorId: string;
  operatorDisplayName: string;
  currencyName: string;
  currencyIconUrl?: string;
  balance: number;
  updatedAt: string;
  characters: Array<{ characterId: string; characterName: string; thumbnailUrl?: string }>;
}
export interface OrphanCharacter {
  characterId: string;
  characterName: string;
  thumbnailUrl?: string;
}
export interface WalletResponse {
  platform: PlatformBalance;
  /** v4 canonical chat-pay scope — one card per operator. */
  operators: OperatorCard[];
  /** Owned characters whose `customerId` is missing (no operator bound).
   *  Rare; surfaced so the fan can see them in the UI. */
  orphanCharacters: OrphanCharacter[];
  /** Legacy group wallets — kept for back-compat read. */
  groups: GroupBalance[];
  /** Legacy v3 field, empty after the per-fan migration runs on
   *  /wallet GET. Kept so old SPA bundles don't crash. */
  characters?: CharacterBalance[];
}
export async function getMyWallet(): Promise<WalletResponse> {
  return request<WalletResponse>('/api/fan/wallet');
}

/** v3: Transfer 平台 → character. */
export async function transferPlatformToCharacter(characterId: string, amount: number): Promise<{
  platformBalance: number;
  characterBalance: number;
}> {
  return request('/api/fan/wallet/transfer', {
    method: 'POST',
    body: JSON.stringify({ characterId, amount }),
  });
}

/** Deprecated — Transfer 平台 → operator (pre-v3 flow). */
export async function transferPlatformToOperator(operatorId: string, amount: number): Promise<{
  platformBalance: number;
  operatorBalance: number;
}> {
  return request('/api/fan/wallet/transfer', {
    method: 'POST',
    body: JSON.stringify({ operatorId, amount }),
  });
}
export interface LedgerRow {
  txnId: string;
  ts: string;
  type: string;
  amount: number;
  balanceAfter: number;
  scope: 'platform' | 'group';
  groupId?: string;
  characterId?: string;
  note?: string;
}
export async function listMyLedger(opts: { scope: 'platform' | 'group'; groupId?: string; limit?: number }): Promise<{ items: LedgerRow[]; nextCursor?: string }> {
  const q = new URLSearchParams();
  q.set('scope', opts.scope);
  if (opts.groupId) q.set('groupId', opts.groupId);
  if (opts.limit) q.set('limit', String(opts.limit));
  return request(`/api/fan/wallet/ledger?${q.toString()}`);
}

// ── Public character info (for 3D rendering) ────────────────────
// Anonymous-OK endpoint; returns full model3d data needed to mount
// the runtime. Matches PublicCharacterInfoSchema in shared-schema.
//
// `sceneObjects` is the COMPOSED list — for authed owners it is
// operatorBase ⊕ fanOverlay (see fan-scene-customization spec);
// anonymous + non-owner callers receive just the operator base.
export interface PublicSceneObject {
  id: string;
  label?: string;
  modelPath: string;
  modelFormat: 'glb' | 'fbx';
  kind: 'static' | 'dynamic';
  visibility: 'always' | 'ai-controlled';
  aiDescription?: string;
  transform: {
    position: [number, number, number];
    rotation: [number, number, number];
    scale: [number, number, number];
  };
  attachment?: { boneName: string };
  hasOwnAnimation?: boolean;
}
export interface PublicCharacterInfo {
  id: string;
  slug: string;
  name: string;
  type: 'pngtuber' | 'model3d' | 'live2d';
  model3d: {
    modelPath: string;
    modelFormat?: 'glb' | 'fbx';
    animationMap?: Record<string, string>;
    texturePack?: unknown;
    morphPack?: unknown;
    expressionMode?: 'texturePack' | 'morphTargets';
    clipLabels?: Record<string, string>;
    defaultClipLabels?: string[];
    oneShotClipLabels?: string[];
    renderParams?: Record<string, number>;
    viewportPose?: { rotationY?: number };
    autoReturnToNature?: boolean;
    sceneObjects?: PublicSceneObject[];
  } | null;
  hasRig: boolean;
  viewport: { width: number; height: number };
}

export async function getPublicInfo(characterId: string): Promise<PublicCharacterInfo> {
  const res = await fetch(`${apiBase()}/api/characters/${encodeURIComponent(characterId)}/public-info`);
  if (!res.ok) {
    throw new Error(`${res.status} ${res.statusText}`);
  }
  return (await res.json()) as PublicCharacterInfo;
}

/** Fetch a presigned download URL for the character's 3D model.
 *  Free mode → 200 with { url, filename }. Paid mode → 402 with
 *  priceTwd in error.details. 401 → redirect to sign-in. */
export async function getModelDownload(characterId: string): Promise<{ url: string; filename: string; expiresInSeconds: number }> {
  const headers = await authHeaders();
  const res = await fetch(
    `${apiBase()}/api/fan/characters/${encodeURIComponent(characterId)}/model-download`,
    { headers },
  );
  if (res.status === 401) {
    if (fanCognitoConfigured()) {
      bounceToLogin();
      return new Promise(() => { /* never resolves; page navigates away */ });
    }
  }
  if (!res.ok) {
    let body: { error?: { code?: string; message?: string; details?: { priceTwd?: number } } } = {};
    try { body = await res.json(); } catch { /* ignore */ }
    const err = new Error(body.error?.message ?? `${res.status} ${res.statusText}`);
    (err as Error & { code?: string; priceTwd?: number }).code = body.error?.code;
    (err as Error & { code?: string; priceTwd?: number }).priceTwd = body.error?.details?.priceTwd;
    throw err;
  }
  return (await res.json()) as { url: string; filename: string; expiresInSeconds: number };
}

/** @deprecated use acquireCharacter() instead. */
export async function enrollInCharacter(characterId: string): Promise<unknown> {
  return acquireCharacter(characterId);
}

// ── Activities (campaign popup) ──────────────────────────────────
export interface ActivityPublic {
  activityId: string;
  name: string;
  description: string;
  trigger: 'signup' | 'first-topup' | 'any-spend';
  reward: { type: 'platform-points' | 'group-points'; amount: number; groupId?: string };
  popupConfig: { ctaLabel: string; ctaAction: 'topup' | 'browse' | 'dismiss' };
  endsAt: string;
}

export async function getEligibleActivities(): Promise<{ items: ActivityPublic[] }> {
  return request<{ items: ActivityPublic[] }>('/api/fan/activities/eligible');
}

export async function claimActivity(activityId: string): Promise<{
  claimed: boolean;
  alreadyClaimed?: boolean;
  reward?: { type: string; amount: number; groupId?: string };
}> {
  return request(`/api/fan/activities/${encodeURIComponent(activityId)}/claim`, { method: 'POST', body: '{}' });
}

// ── Chat ───────────────────────────────────────────────────────
export interface ChatMessage { role: 'user' | 'assistant'; content: string }
export interface ChatReply {
  text: string;
  expression?: string;
  actions?: unknown[];
}
export interface ChatErrorDetails {
  currentBalance?: number;
  requiredAmount?: number;
  groupId?: string;
  reason?: string;
}
export class ChatApiError extends Error {
  status: number;
  code: string;
  details?: ChatErrorDetails;
  constructor(status: number, code: string, message: string, details?: ChatErrorDetails) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

export async function sendChatMessage(
  characterId: string,
  messages: ChatMessage[],
  sessionId?: string,
): Promise<ChatReply> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(await authHeaders()),
  };
  const res = await fetch(`${apiBase()}/api/characters/${encodeURIComponent(characterId)}/chat`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ messages, sessionId }),
  });
  if (!res.ok) {
    let body: { error?: { code?: string; message?: string; details?: ChatErrorDetails } } = {};
    try { body = await res.json(); } catch { /* non-json */ }
    const code = body.error?.code ?? `HTTP_${res.status}`;
    const message = body.error?.message ?? res.statusText;
    const details = body.error?.details ?? {
      currentBalance: (body.error as unknown as ChatErrorDetails | undefined)?.currentBalance,
      requiredAmount: (body.error as unknown as ChatErrorDetails | undefined)?.requiredAmount,
      groupId: (body.error as unknown as ChatErrorDetails | undefined)?.groupId,
    };
    throw new ChatApiError(res.status, code, message, details);
  }
  return (await res.json()) as ChatReply;
}

// ── Topup ──────────────────────────────────────────────────────
export interface TopupCreatePayload {
  groupId: string;
  amount: number;
  pointsAmount: number;
}
export interface TopupCreateResponse {
  tradeNo: string;
  redirectUrl: string;
  formFields: Record<string, string>;
}
export async function createTopup(p: TopupCreatePayload): Promise<TopupCreateResponse> {
  return request<TopupCreateResponse>('/api/fan/wallet/topup/create', {
    method: 'POST',
    body: JSON.stringify(p),
  });
}

// ── 物件庫 ─────────────────────────────────────────────────────
export interface LibraryObject {
  objectId: string;
  label: string;
  modelPath: string;
  modelFormat: 'glb' | 'fbx';
  size: number;
  tags?: string[];
  pricePoints: number;
  thumbnail?: string;
  // object-library-v2
  scope?: 'public' | 'operator' | 'character';
  frontStatus?: 'listed' | 'preview' | 'backend';
  description?: string;
}
export async function listLibraryObjects(opts?: { characterId?: string }): Promise<{ items: LibraryObject[] }> {
  // object-library-v2 — the server now filters by caller scope +
  // front-status (backend rows are never returned to fans). When
  // browsing in a character's context, pass characterId so that
  // character's character-scoped objects are included.
  const qs = opts?.characterId ? `?characterId=${encodeURIComponent(opts.characterId)}` : '';
  return request<{ items: LibraryObject[] }>(`/api/library/objects${qs}`);
}

export interface OwnedObject {
  objectId: string;
  acquiredAt: string;
  pricePaidPoints: number;
  paidWith: { scope: 'platform' | 'operator' | 'group'; scopedId?: string };
  label?: string;
  modelPath?: string;
  modelFormat?: 'glb' | 'fbx';
  tags?: string[];
}
export async function listMyFanObjects(): Promise<{ items: OwnedObject[] }> {
  return request<{ items: OwnedObject[] }>('/api/fan/objects');
}

export interface PurchaseObjectPayload {
  walletScope: 'platform' | 'operator';
  operatorId?: string;
}
export async function purchaseFanObject(objectId: string, p: PurchaseObjectPayload): Promise<{
  purchased: boolean;
  alreadyOwned?: boolean;
  pricePaidPoints?: number;
}> {
  return request(`/api/fan/objects/${encodeURIComponent(objectId)}/purchase`, {
    method: 'POST',
    body: JSON.stringify(p),
  });
}

export interface AttachSceneObjectPayload {
  objectId: string;
  label: string;
  kind: 'static' | 'dynamic';
  transform: {
    position: [number, number, number];
    rotation: [number, number, number];
    scale: [number, number, number];
  };
  attachment?: { boneName: string };
  visibility?: 'always' | 'ai-controlled';
}
export interface AttachedSceneObject extends AttachSceneObjectPayload {
  id: string;
  modelPath: string;
  modelFormat: 'glb' | 'fbx';
  attachedBy: string;
  attachedAt: string;
}
export async function attachSceneObjectToCharacter(
  characterId: string,
  payload: AttachSceneObjectPayload,
): Promise<{ sceneObject: AttachedSceneObject }> {
  return request(`/api/fan/characters/${encodeURIComponent(characterId)}/scene-objects`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}
export async function detachSceneObjectFromCharacter(
  characterId: string,
  sceneObjectId: string,
): Promise<{ removed: boolean }> {
  return request(
    `/api/fan/characters/${encodeURIComponent(characterId)}/scene-objects/${encodeURIComponent(sceneObjectId)}`,
    { method: 'DELETE' },
  );
}

// ── Per-fan scene overlay (decorate page) ────────────────────────
/** Per `fan-scene-customization` — one entry of a fan's overlay. Mirrors
 *  the operator scene-object schema with no `aiDescription` and
 *  `visibility` locked to 'always'. */
export interface FanSceneItem {
  id: string;
  libraryId?: string;
  label: string;
  modelPath: string;
  modelFormat: 'glb' | 'fbx';
  kind: 'static' | 'dynamic';
  visibility: 'always';
  transform: {
    position: [number, number, number];
    rotation: [number, number, number];
    scale: [number, number, number];
  };
  attachment?: { boneName: string };
  hasOwnAnimation?: boolean;
}
export async function getMyScene(characterId: string): Promise<{ items: FanSceneItem[] }> {
  return request<{ items: FanSceneItem[] }>(
    `/api/fan/characters/${encodeURIComponent(characterId)}/my-scene`,
  );
}
export async function putMyScene(
  characterId: string,
  items: FanSceneItem[],
): Promise<{ items: FanSceneItem[] }> {
  return request<{ items: FanSceneItem[] }>(
    `/api/fan/characters/${encodeURIComponent(characterId)}/my-scene`,
    { method: 'PUT', body: JSON.stringify({ items }) },
  );
}

// ── Top-up packages (ecpay-topup capability) ───────────────────────
/**
 * Public-facing topup package (active rows only). Bonus implied by
 * `points - amountNtd` — UI computes display percentage from these
 * two numbers, no separate "bonus" field stored.
 */
export interface TopupPackagePublic {
  sku: string;
  amountNtd: number;
  points: number;
  displayOrder: number;
  active: boolean;
  label?: string;
  badge?: string;
}

export async function getTopupPackages(): Promise<{ items: TopupPackagePublic[] }> {
  // Public — uses anonymous-OK path; the `allow401:true` flag lets
  // the request() helper short-circuit the 401 redirect that's the
  // default for the fan SPA's authed paths.
  return request<{ items: TopupPackagePublic[] }>(
    '/api/topup-packages',
    { allow401: true },
  );
}

/**
 * Server returns the ECPay AIO Checkout form fields. Caller builds
 * a hidden `<form>` and submits — browser then redirects to ECPay's
 * payment page.
 */
export interface TopupCreateResult {
  url: string;             // ECPay AioCheckOut endpoint
  params: Record<string, string>;  // form fields incl. CheckMacValue
  MerchantTradeNo: string;
}

export async function createTopupOrder(sku: string): Promise<TopupCreateResult> {
  return request<TopupCreateResult>('/api/fan/wallet/topup/create', {
    method: 'POST',
    body: JSON.stringify({ sku }),
  });
}

export interface TopupStatusResult {
  status: 'pending' | 'success' | 'failed' | 'refunded' | string;
  amountTwd: number;
  pointsCredited: number;
  ecpayTradeNo?: string;
  invoiceNumber?: string;
  updatedAt: string;
}

export async function getTopupStatus(tradeNo: string): Promise<TopupStatusResult> {
  return request<TopupStatusResult>(`/api/fan/wallet/topup/${encodeURIComponent(tradeNo)}`);
}
